import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, ChevronUp, Upload, Check, Loader2, X,
  Zap, Eye, Network, Lock, Wifi, User, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MOVED OUTSIDE - Checkbox component with LIGHT style
const FormCheckbox = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
    <input 
      type="checkbox" 
      checked={checked} 
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 rounded border-2 border-gray-300 bg-white checked:bg-orange-500 checked:border-orange-500 focus:ring-orange-500 focus:ring-2 cursor-pointer appearance-none relative
        after:content-[''] after:absolute after:hidden checked:after:block after:left-[5px] after:top-[2px] after:w-[5px] after:h-[8px] after:border-white after:border-r-2 after:border-b-2 after:rotate-45"
      style={{ accentColor: '#f97316' }}
    />
    <span>{label}</span>
  </label>
);

// MOVED OUTSIDE - Text Input component  
const FormTextInput = ({ label, value, onChange, placeholder = '' }) => (
  <div className="space-y-1">
    <Label className="text-xs text-gray-600 block">{label}</Label>
    <input 
      type="text"
      value={value} 
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>
);

// MOVED OUTSIDE - Number Input component
const FormNumberInput = ({ label, value, onChange, unit = '' }) => (
  <div className="space-y-1">
    <Label className="text-xs text-gray-600 block">{label}</Label>
    <div className="relative">
      <input 
        type="text"
        value={value} 
        onChange={(e) => onChange(e.target.value)}
        className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
      />
      {unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{unit}</span>}
    </div>
  </div>
);

export default function Quote() {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [expandedServices, setExpandedServices] = useState({});
  
  const [formData, setFormData] = useState({
    // Contact Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    whatsapp: '',
    companyName: '',
    projectDescription: '',
    cityAddress: '',
    estimatedBudget: '',
    // SMS Consent
    smsConsent: false,
    // Technical Advisory
    needsVideoCall: false,
    needsSiteSurvey: false,
    selectedServices: [],
    // Fiber Optic
    fiber: {
      includeLabor: false, includeMaterials: false,
      fiberType: { singleMode: false, multiMode: false },
      fiberStrands: '0', splicingQty: '0', mechanicalConnectors: '0', fiberLength: '0',
      terminationType: { lc: false, fc: false, sc: false, st: false },
      odfTypeQty: '0', portQuantity: '0'
    },
    // CCTV
    cctv: {
      includeLabor: false, includeMaterials: false, needsConfig: false,
      nvrChannels: '0', diskCapacity: '0', analogCameras: '0', ipCameras: '0',
      resolution: '', workHeight: '0',
      location: { interior: false, exterior: false },
      cableType: { cat5e: false, cat6: false, cat6a: false },
      other: { pvc: false, emt: false, none: false },
      conduit: false
    },
    // Structured Cabling
    cabling: {
      includeLabor: false, includeMaterials: false, needsUPS: false,
      cat5Points: '0', cat6aPoints: '0', cat6Points: '0',
      rackUnits: '0', emtQty: '0', pvcQty: '0', escalerillQty: '0',
      projectType: { residential: false, commercial: false },
      construction: { newBuild: false, renovation: false }
    },
    // Access Control
    access: {
      includeLabor: false, includeMaterials: false, needsConfig: false,
      doorType: '', doorCount: '0', cardReaders: '0', electromagnets: '0',
      electroLocks: '0', openingSensors: '0', exitButtons: '0', doorContacts: '0'
    },
    // Networking
    network: {
      includeLabor: false, includeDevices: false, includeCabling: false,
      serviceType: { installation: false, networkDesign: false, configuration: false },
      switchCount: '0', portsPerSwitch: '0', firewalls: '0', routers: '0',
      accessPoints: '0', servers: '0', computers: '0'
    }
  });

  // Services list
  const services = [
    { id: 'fiber', name: 'Fiber Optic Installation & Splicing', nameEs: 'Instalación y Fusión de Fibra Óptica', icon: Zap },
    { id: 'cctv', name: 'CCTV Systems', nameEs: 'Sistemas CCTV', icon: Eye },
    { id: 'cabling', name: 'Structured Cabling', nameEs: 'Cableado Estructurado', icon: Network },
    { id: 'access', name: 'Access Control', nameEs: 'Control de Acceso', icon: Lock },
    { id: 'network', name: 'Networking Solutions', nameEs: 'Soluciones de Red', icon: Wifi }
  ];

  // Pre-select service from URL
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    if (serviceParam) {
      const validServices = ['fiber', 'cctv', 'cabling', 'access', 'network'];
      if (validServices.includes(serviceParam)) {
        setFormData(prev => ({ ...prev, selectedServices: [serviceParam] }));
        setExpandedServices({ [serviceParam]: true });
      }
    }
  }, []);

  const toggleService = (serviceId) => {
    const isCurrentlySelected = formData.selectedServices.includes(serviceId);
    if (isCurrentlySelected) {
      setFormData(prev => ({
        ...prev,
        selectedServices: prev.selectedServices.filter(s => s !== serviceId)
      }));
      setExpandedServices(prev => ({ ...prev, [serviceId]: false }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedServices: [...prev.selectedServices, serviceId]
      }));
      setExpandedServices(prev => ({ ...prev, [serviceId]: true }));
      // Auto-scroll to show the expanded service
      setTimeout(() => {
        const element = document.getElementById(`service-${serviceId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const toggleExpand = (serviceId) => {
    if (formData.selectedServices.includes(serviceId)) {
      setExpandedServices(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));
    }
  };

  const updateField = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const updateNestedField = (section, parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parent]: { ...prev[section][parent], [field]: value }
      }
    }));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles].slice(0, 5));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Transform formData to backend format
      const servicesData = formData.selectedServices.map(serviceId => {
        const service = services.find(s => s.id === serviceId);
        return {
          service_id: serviceId,
          service_name: service ? service.name : serviceId,
          quantity: 1,
          details: JSON.stringify(formData[serviceId] || {})
        };
      });

      // Build advisory notes
      let advisoryNotes = [];
      if (formData.needsVideoCall) advisoryNotes.push('📹 Solicita Video Llamada');
      if (formData.needsSiteSurvey) advisoryNotes.push('📍 Solicita Site Survey');
      
      const fullNotes = [
        formData.projectDescription,
        advisoryNotes.length > 0 ? `\n\nAsesoría Técnica: ${advisoryNotes.join(', ')}` : ''
      ].filter(Boolean).join('');

      const payload = {
        customer_name: `${formData.firstName} ${formData.lastName}`.trim(),
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_whatsapp: formData.whatsapp || formData.phone,
        company: formData.companyName || null,
        city: formData.cityAddress || 'Florida',
        address: formData.cityAddress || null,
        services: servicesData,
        project_type: 'commercial',
        timeline: 'flexible',
        budget_range: formData.estimatedBudget || null,
        additional_notes: fullNotes || null,
        language: language
      };

      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        toast.success(isEs ? '¡Cotización enviada exitosamente!' : 'Quote submitted successfully!');
        
        // Send webhook to external CRM (non-blocking) - EXACT FORMAT REQUIRED BY CRM
        try {
          // Build services object with enabled flags
          const servicesObj = {
            cctv: { enabled: formData.selectedServices.includes('cctv'), ...formData.cctv },
            fiber: { enabled: formData.selectedServices.includes('fiber'), ...formData.fiber },
            cabling: { enabled: formData.selectedServices.includes('cabling'), ...formData.cabling },
            access: { enabled: formData.selectedServices.includes('access'), ...formData.access },
            network: { enabled: formData.selectedServices.includes('network'), ...formData.network }
          };
          
          // Build advisory array
          const advisory = [];
          if (formData.needsVideoCall) advisory.push('Video Call');
          if (formData.needsSiteSurvey) advisory.push('Site Survey');
          
          const crmPayload = {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            whatsapp: formData.whatsapp || formData.phone,
            company: formData.companyName || '',
            project_description: formData.projectDescription || '',
            city_address: formData.cityAddress || '',
            budget: formData.estimatedBudget || '',
            services: servicesObj,
            advisory: advisory,
            language: language,
            source: 'website_quote_form',
            timestamp: new Date().toISOString()
          };
          
          fetch('https://crm.iqeslowvoltage.com/api/webhooks/quote-request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(crmPayload)
          }).catch(err => console.log('CRM webhook error (non-blocking):', err));
        } catch (webhookError) {
          console.log('CRM webhook preparation error:', webhookError);
        }
        // Reset form
        setFormData(prev => ({
          ...prev,
          firstName: '', lastName: '', email: '', phone: '',
          companyName: '', projectDescription: '', cityAddress: '', estimatedBudget: '',
          needsVideoCall: false, needsSiteSurvey: false,
          selectedServices: []
        }));
        setExpandedServices({});
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Quote error:', errorData);
        throw new Error('Failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(isEs ? 'Error al enviar' : 'Error submitting quote');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-20">
      {/* Hero */}
      <section className="py-6" style={{ backgroundColor: '#003366' }}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">
            {isEs ? 'Solicitar Cotización' : 'Request Quote'}
          </h1>
          <p className="text-white/70 text-sm">
            {isEs ? 'Complete el formulario para recibir su cotización' : 'Fill the form to receive your quote'}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-6">
        <div className="container mx-auto px-4 max-w-4xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5" style={{ color: '#FF6B00' }} />
                <h2 className="font-semibold text-gray-800">{isEs ? 'Información de Contacto' : 'Contact Information'}</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormTextInput 
                  label={isEs ? 'Nombre *' : 'First Name *'}
                  value={formData.firstName}
                  onChange={(v) => updateField(null, 'firstName', v)}
                  placeholder={isEs ? 'Su nombre' : 'Your first name'}
                />
                <FormTextInput 
                  label={isEs ? 'Apellido *' : 'Last Name *'}
                  value={formData.lastName}
                  onChange={(v) => updateField(null, 'lastName', v)}
                  placeholder={isEs ? 'Su apellido' : 'Your last name'}
                />
                <FormTextInput 
                  label={isEs ? 'Correo Electrónico *' : 'Email *'}
                  value={formData.email}
                  onChange={(v) => updateField(null, 'email', v)}
                  placeholder="email@example.com"
                />
                <FormTextInput 
                  label={isEs ? 'Teléfono *' : 'Phone *'}
                  value={formData.phone}
                  onChange={(v) => updateField(null, 'phone', v)}
                  placeholder="(XXX) XXX-XXXX"
                />
                <FormTextInput 
                  label={isEs ? 'WhatsApp (Opcional)' : 'WhatsApp (Optional)'}
                  value={formData.whatsapp}
                  onChange={(v) => updateField(null, 'whatsapp', v)}
                  placeholder={isEs ? 'Si es diferente al teléfono' : 'If different from phone'}
                />
              </div>
              
              <div className="mt-4">
                <FormTextInput 
                  label={isEs ? 'Empresa (Opcional)' : 'Company (Optional)'}
                  value={formData.companyName}
                  onChange={(v) => updateField(null, 'companyName', v)}
                  placeholder={isEs ? 'Nombre de su empresa' : 'Your company name'}
                />
              </div>
            </div>

            {/* Select Services */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5" style={{ color: '#FF6B00' }} />
                <h2 className="font-semibold text-gray-800">{isEs ? 'Seleccione los Servicios *' : 'Select Services *'}</h2>
              </div>

              <div className="space-y-3">
                {services.map((service) => {
                  const isSelected = formData.selectedServices.includes(service.id);
                  const isExpanded = expandedServices[service.id];
                  const Icon = service.icon;

                  return (
                    <div key={service.id} id={`service-${service.id}`} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Service Header */}
                      <div className={`flex items-center justify-between p-3 ${isSelected ? 'bg-orange-50' : 'bg-white'}`}>
                        <div className="flex items-center gap-3">
                          <div 
                            onClick={() => toggleService(service.id)}
                            className={`w-5 h-5 rounded border-2 cursor-pointer flex items-center justify-center transition-all
                              ${isSelected 
                                ? 'bg-orange-500 border-orange-500' 
                                : 'bg-white border-gray-300 hover:border-orange-400'}`}
                          >
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="font-medium text-sm text-gray-800">
                            {isEs ? service.nameEs : service.name}
                          </span>
                        </div>
                        {isSelected && (
                          <button type="button" onClick={() => toggleExpand(service.id)}>
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                          </button>
                        )}
                      </div>

                      {/* Service Details */}
                      <AnimatePresence>
                        {isSelected && isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-gray-200 bg-gray-50 p-4"
                          >
                            {/* FIBER OPTIC */}
                            {service.id === 'fiber' && (
                              <div className="space-y-4">
                                <div className="flex flex-wrap gap-4">
                                  <FormCheckbox checked={formData.fiber.includeLabor} onChange={(v) => updateField('fiber', 'includeLabor', v)} label={isEs ? 'Incluir Mano de Obra' : 'Include Labor'} />
                                  <FormCheckbox checked={formData.fiber.includeMaterials} onChange={(v) => updateField('fiber', 'includeMaterials', v)} label={isEs ? 'Incluir Materiales' : 'Include Materials'} />
                                </div>
                                
                                <div>
                                  <Label className="text-xs text-gray-600 mb-2 block">{isEs ? 'Tipo de Fibra' : 'Fiber Type'}</Label>
                                  <div className="flex gap-4">
                                    <FormCheckbox checked={formData.fiber.fiberType.singleMode} onChange={(v) => updateNestedField('fiber', 'fiberType', 'singleMode', v)} label="Single Mode" />
                                    <FormCheckbox checked={formData.fiber.fiberType.multiMode} onChange={(v) => updateNestedField('fiber', 'fiberType', 'multiMode', v)} label="Multi Mode" />
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  <FormNumberInput label={isEs ? '# de Hilos' : '# of Strands'} value={formData.fiber.fiberStrands} onChange={(v) => updateField('fiber', 'fiberStrands', v)} />
                                  <FormNumberInput label={isEs ? 'Cantidad Splicing' : 'Splicing Qty'} value={formData.fiber.splicingQty} onChange={(v) => updateField('fiber', 'splicingQty', v)} />
                                  <FormNumberInput label={isEs ? 'Conectores Mecánicos' : 'Mechanical Connectors'} value={formData.fiber.mechanicalConnectors} onChange={(v) => updateField('fiber', 'mechanicalConnectors', v)} />
                                  <FormNumberInput label={isEs ? 'Longitud de Fibra' : 'Fiber Length'} value={formData.fiber.fiberLength} onChange={(v) => updateField('fiber', 'fiberLength', v)} unit="ft" />
                                </div>

                                <div>
                                  <Label className="text-xs text-gray-600 mb-2 block">{isEs ? 'Tipo de Terminación' : 'Termination Type'}</Label>
                                  <div className="flex flex-wrap gap-4">
                                    <FormCheckbox checked={formData.fiber.terminationType.lc} onChange={(v) => updateNestedField('fiber', 'terminationType', 'lc', v)} label="LC" />
                                    <FormCheckbox checked={formData.fiber.terminationType.fc} onChange={(v) => updateNestedField('fiber', 'terminationType', 'fc', v)} label="FC" />
                                    <FormCheckbox checked={formData.fiber.terminationType.sc} onChange={(v) => updateNestedField('fiber', 'terminationType', 'sc', v)} label="SC" />
                                    <FormCheckbox checked={formData.fiber.terminationType.st} onChange={(v) => updateNestedField('fiber', 'terminationType', 'st', v)} label="ST" />
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <FormNumberInput label={isEs ? 'Tipo/Cantidad ODF' : 'ODF Type/Qty'} value={formData.fiber.odfTypeQty} onChange={(v) => updateField('fiber', 'odfTypeQty', v)} />
                                  <FormNumberInput label={isEs ? 'Cantidad de Puertos' : 'Port Quantity'} value={formData.fiber.portQuantity} onChange={(v) => updateField('fiber', 'portQuantity', v)} />
                                </div>
                              </div>
                            )}

                            {/* CCTV */}
                            {service.id === 'cctv' && (
                              <div className="space-y-4">
                                <div className="flex flex-wrap gap-4">
                                  <FormCheckbox checked={formData.cctv.includeLabor} onChange={(v) => updateField('cctv', 'includeLabor', v)} label={isEs ? 'Incluir Mano de Obra' : 'Include Labor'} />
                                  <FormCheckbox checked={formData.cctv.includeMaterials} onChange={(v) => updateField('cctv', 'includeMaterials', v)} label={isEs ? 'Incluir Materiales' : 'Include Materials'} />
                                  <FormCheckbox checked={formData.cctv.needsConfig} onChange={(v) => updateField('cctv', 'needsConfig', v)} label={isEs ? 'Necesita Configuración' : 'Needs Configuration'} />
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  <FormNumberInput label={isEs ? 'Canales NVR' : 'NVR Channels'} value={formData.cctv.nvrChannels} onChange={(v) => updateField('cctv', 'nvrChannels', v)} />
                                  <FormNumberInput label={isEs ? 'Capacidad Disco' : 'Disk Capacity'} value={formData.cctv.diskCapacity} onChange={(v) => updateField('cctv', 'diskCapacity', v)} unit="TB" />
                                  <FormNumberInput label={isEs ? 'Cámaras Análogas' : 'Analog Cameras'} value={formData.cctv.analogCameras} onChange={(v) => updateField('cctv', 'analogCameras', v)} />
                                  <FormNumberInput label={isEs ? 'Cámaras IP' : 'IP Cameras'} value={formData.cctv.ipCameras} onChange={(v) => updateField('cctv', 'ipCameras', v)} />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <FormTextInput label={isEs ? 'Resolución' : 'Resolution'} value={formData.cctv.resolution} onChange={(v) => updateField('cctv', 'resolution', v)} placeholder="4K, 2K, 1080p..." />
                                  <FormNumberInput label={isEs ? 'Altura de Trabajo' : 'Work Height'} value={formData.cctv.workHeight} onChange={(v) => updateField('cctv', 'workHeight', v)} unit="ft" />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-xs text-gray-600 mb-2 block">{isEs ? 'Ubicación' : 'Location'}</Label>
                                    <div className="flex gap-4">
                                      <FormCheckbox checked={formData.cctv.location.interior} onChange={(v) => updateNestedField('cctv', 'location', 'interior', v)} label={isEs ? 'Interior' : 'Indoor'} />
                                      <FormCheckbox checked={formData.cctv.location.exterior} onChange={(v) => updateNestedField('cctv', 'location', 'exterior', v)} label={isEs ? 'Exterior' : 'Outdoor'} />
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-gray-600 mb-2 block">{isEs ? 'Tipo de Cable' : 'Cable Type'}</Label>
                                    <div className="flex gap-4">
                                      <FormCheckbox checked={formData.cctv.cableType.cat5e} onChange={(v) => updateNestedField('cctv', 'cableType', 'cat5e', v)} label="Cat5e" />
                                      <FormCheckbox checked={formData.cctv.cableType.cat6} onChange={(v) => updateNestedField('cctv', 'cableType', 'cat6', v)} label="Cat6" />
                                      <FormCheckbox checked={formData.cctv.cableType.cat6a} onChange={(v) => updateNestedField('cctv', 'cableType', 'cat6a', v)} label="Cat6A" />
                                    </div>
                                  </div>
                                </div>

                                <div className="flex gap-4">
                                  <FormCheckbox checked={formData.cctv.other.pvc} onChange={(v) => updateNestedField('cctv', 'other', 'pvc', v)} label="PVC" />
                                  <FormCheckbox checked={formData.cctv.other.emt} onChange={(v) => updateNestedField('cctv', 'other', 'emt', v)} label="EMT" />
                                  <FormCheckbox checked={formData.cctv.other.none} onChange={(v) => updateNestedField('cctv', 'other', 'none', v)} label={isEs ? 'Ninguno' : 'None'} />
                                </div>

                                <FormCheckbox checked={formData.cctv.conduit} onChange={(v) => updateField('cctv', 'conduit', v)} label={isEs ? 'Tubería' : 'Conduit'} />
                              </div>
                            )}

                            {/* STRUCTURED CABLING */}
                            {service.id === 'cabling' && (
                              <div className="space-y-4">
                                <div className="flex flex-wrap gap-4">
                                  <FormCheckbox checked={formData.cabling.includeLabor} onChange={(v) => updateField('cabling', 'includeLabor', v)} label={isEs ? 'Incluir Mano de Obra' : 'Include Labor'} />
                                  <FormCheckbox checked={formData.cabling.includeMaterials} onChange={(v) => updateField('cabling', 'includeMaterials', v)} label={isEs ? 'Incluir Materiales' : 'Include Materials'} />
                                  <FormCheckbox checked={formData.cabling.needsUPS} onChange={(v) => updateField('cabling', 'needsUPS', v)} label={isEs ? 'Necesita UPS' : 'Needs UPS'} />
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                  <FormNumberInput label={isEs ? 'Puntos Cat5' : 'Cat5 Points'} value={formData.cabling.cat5Points} onChange={(v) => updateField('cabling', 'cat5Points', v)} />
                                  <FormNumberInput label={isEs ? 'Puntos Cat6A' : 'Cat6A Points'} value={formData.cabling.cat6aPoints} onChange={(v) => updateField('cabling', 'cat6aPoints', v)} />
                                  <FormNumberInput label={isEs ? 'Puntos Cat6?' : 'Cat6 Points?'} value={formData.cabling.cat6Points} onChange={(v) => updateField('cabling', 'cat6Points', v)} />
                                </div>

                                <div className="grid grid-cols-4 gap-3">
                                  <FormNumberInput label={isEs ? 'Unidades de Rack' : 'Rack Units'} value={formData.cabling.rackUnits} onChange={(v) => updateField('cabling', 'rackUnits', v)} unit="RU" />
                                  <FormNumberInput label={isEs ? 'CTD de EMT' : 'EMT Qty'} value={formData.cabling.emtQty} onChange={(v) => updateField('cabling', 'emtQty', v)} />
                                  <FormNumberInput label={isEs ? 'CTD PVC' : 'PVC Qty'} value={formData.cabling.pvcQty} onChange={(v) => updateField('cabling', 'pvcQty', v)} />
                                  <FormNumberInput label={isEs ? 'CTD de Escalerilla' : 'Cable Tray Qty'} value={formData.cabling.escalerillQty} onChange={(v) => updateField('cabling', 'escalerillQty', v)} />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-xs text-gray-600 mb-2 block">{isEs ? 'Tipo de Proyecto' : 'Project Type'}</Label>
                                    <div className="flex flex-wrap gap-2">
                                      <FormCheckbox checked={formData.cabling.projectType.residential} onChange={(v) => updateNestedField('cabling', 'projectType', 'residential', v)} label={isEs ? 'Residencial' : 'Residential'} />
                                      <FormCheckbox checked={formData.cabling.projectType.commercial} onChange={(v) => updateNestedField('cabling', 'projectType', 'commercial', v)} label={isEs ? 'Comercial' : 'Commercial'} />
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-gray-600 mb-2 block">{isEs ? 'Construcción' : 'Construction'}</Label>
                                    <div className="flex flex-wrap gap-2">
                                      <FormCheckbox checked={formData.cabling.construction.newBuild} onChange={(v) => updateNestedField('cabling', 'construction', 'newBuild', v)} label={isEs ? 'Nueva' : 'New'} />
                                      <FormCheckbox checked={formData.cabling.construction.renovation} onChange={(v) => updateNestedField('cabling', 'construction', 'renovation', v)} label={isEs ? 'Remodelación' : 'Renovation'} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* ACCESS CONTROL */}
                            {service.id === 'access' && (
                              <div className="space-y-4">
                                <div className="flex flex-wrap gap-4">
                                  <FormCheckbox checked={formData.access.includeLabor} onChange={(v) => updateField('access', 'includeLabor', v)} label={isEs ? 'Incluir Mano de Obra' : 'Include Labor'} />
                                  <FormCheckbox checked={formData.access.includeMaterials} onChange={(v) => updateField('access', 'includeMaterials', v)} label={isEs ? 'Incluir Materiales' : 'Include Materials'} />
                                  <FormCheckbox checked={formData.access.needsConfig} onChange={(v) => updateField('access', 'needsConfig', v)} label={isEs ? 'Necesita Configuración' : 'Needs Configuration'} />
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  <FormTextInput label={isEs ? 'Tipo de Puerta' : 'Door Type'} value={formData.access.doorType} onChange={(v) => updateField('access', 'doorType', v)} placeholder={isEs ? 'Vidrio, Metal...' : 'Glass, Metal...'} />
                                  <FormNumberInput label={isEs ? 'Cantidad de Puertas' : 'Door Count'} value={formData.access.doorCount} onChange={(v) => updateField('access', 'doorCount', v)} />
                                  <FormNumberInput label={isEs ? 'Lectores de Tarjeta' : 'Card Readers'} value={formData.access.cardReaders} onChange={(v) => updateField('access', 'cardReaders', v)} />
                                  <FormNumberInput label={isEs ? 'Electroimanes' : 'Electromagnets'} value={formData.access.electromagnets} onChange={(v) => updateField('access', 'electromagnets', v)} />
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  <FormNumberInput label={isEs ? 'Electro Chapas' : 'Electric Strikes'} value={formData.access.electroLocks} onChange={(v) => updateField('access', 'electroLocks', v)} />
                                  <FormNumberInput label={isEs ? 'Sensores de Apertura' : 'Opening Sensors'} value={formData.access.openingSensors} onChange={(v) => updateField('access', 'openingSensors', v)} />
                                  <FormNumberInput label={isEs ? 'Botones de Salida' : 'Exit Buttons'} value={formData.access.exitButtons} onChange={(v) => updateField('access', 'exitButtons', v)} />
                                  <FormNumberInput label={isEs ? 'Contactos de Puerta' : 'Door Contacts'} value={formData.access.doorContacts} onChange={(v) => updateField('access', 'doorContacts', v)} />
                                </div>
                              </div>
                            )}

                            {/* NETWORKING */}
                            {service.id === 'network' && (
                              <div className="space-y-4">
                                <div className="flex flex-wrap gap-4">
                                  <FormCheckbox checked={formData.network.includeLabor} onChange={(v) => updateField('network', 'includeLabor', v)} label={isEs ? 'Incluir Mano de Obra' : 'Include Labor'} />
                                  <FormCheckbox checked={formData.network.includeDevices} onChange={(v) => updateField('network', 'includeDevices', v)} label={isEs ? 'Incluir Dispositivos' : 'Include Devices'} />
                                </div>

                                <div>
                                  <Label className="text-xs text-gray-600 mb-2 block">{isEs ? 'Tipo de Servicio' : 'Service Type'}</Label>
                                  <div className="flex flex-wrap gap-4">
                                    <FormCheckbox checked={formData.network.serviceType.installation} onChange={(v) => updateNestedField('network', 'serviceType', 'installation', v)} label={isEs ? 'Instalación' : 'Installation'} />
                                    <FormCheckbox checked={formData.network.serviceType.networkDesign} onChange={(v) => updateNestedField('network', 'serviceType', 'networkDesign', v)} label={isEs ? 'Diseño de Red' : 'Network Design'} />
                                    <FormCheckbox checked={formData.network.serviceType.configuration} onChange={(v) => updateNestedField('network', 'serviceType', 'configuration', v)} label={isEs ? 'Configuración' : 'Configuration'} />
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  <FormNumberInput label={isEs ? 'Cantidad Switches' : 'Switch Count'} value={formData.network.switchCount} onChange={(v) => updateField('network', 'switchCount', v)} />
                                  <FormNumberInput label={isEs ? 'Puertos por Switch' : 'Ports per Switch'} value={formData.network.portsPerSwitch} onChange={(v) => updateField('network', 'portsPerSwitch', v)} />
                                  <FormNumberInput label="Firewalls" value={formData.network.firewalls} onChange={(v) => updateField('network', 'firewalls', v)} />
                                  <FormNumberInput label="Routers" value={formData.network.routers} onChange={(v) => updateField('network', 'routers', v)} />
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                  <FormNumberInput label="Access Points (AP)" value={formData.network.accessPoints} onChange={(v) => updateField('network', 'accessPoints', v)} />
                                  <FormNumberInput label={isEs ? 'Servidores' : 'Servers'} value={formData.network.servers} onChange={(v) => updateField('network', 'servers', v)} />
                                  <FormNumberInput label={isEs ? 'Computadores' : 'Computers'} value={formData.network.computers} onChange={(v) => updateField('network', 'computers', v)} />
                                </div>

                                {/* Include Cabling Checkbox */}
                                <div className="pt-3 border-t border-gray-200">
                                  <FormCheckbox checked={formData.network.includeCabling} onChange={(v) => updateField('network', 'includeCabling', v)} label={isEs ? 'Incluir Cableado' : 'Include Cabling'} />
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Project Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5" style={{ color: '#FF6B00' }} />
                <h2 className="font-semibold text-gray-800">{isEs ? 'Detalles del Proyecto' : 'Project Details'}</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-600">{isEs ? 'Descripción del Proyecto *' : 'Project Description *'}</Label>
                  <Textarea
                    value={formData.projectDescription}
                    onChange={(e) => updateField(null, 'projectDescription', e.target.value)}
                    placeholder={isEs ? 'Describa los requisitos de su proyecto...' : 'Describe your project requirements...'}
                    className="bg-white border border-gray-300 text-gray-800 min-h-[100px] mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormTextInput 
                    label={isEs ? 'Ciudad o Dirección *' : 'City or Address *'}
                    value={formData.cityAddress}
                    onChange={(v) => updateField(null, 'cityAddress', v)}
                    placeholder={isEs ? 'Ej: Miami, FL o dirección completa' : 'Ex: Miami, FL or full address'}
                  />
                  <FormTextInput 
                    label={isEs ? 'Presupuesto Estimado' : 'Estimated Budget'}
                    value={formData.estimatedBudget}
                    onChange={(v) => updateField(null, 'estimatedBudget', v)}
                    placeholder={isEs ? 'Ej: $5,000 - $10,000' : 'Ex: $5,000 - $10,000'}
                  />
                </div>

                {/* Technical Advisory Options */}
                <div className="rounded-lg p-4" style={{ backgroundColor: '#003366' }}>
                  <p className="text-sm text-white mb-3">
                    {isEs 
                      ? '¿Necesita asesoría técnica? Seleccione una opción:' 
                      : 'Need technical advisory? Select an option:'}
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.needsVideoCall} 
                        onChange={(e) => updateField(null, 'needsVideoCall', e.target.checked)}
                        className="w-4 h-4 rounded border-2 border-white bg-transparent checked:bg-orange-500 checked:border-orange-500 cursor-pointer appearance-none relative
                          after:content-[''] after:absolute after:hidden checked:after:block after:left-[4px] after:top-[1px] after:w-[5px] after:h-[8px] after:border-white after:border-r-2 after:border-b-2 after:rotate-45"
                      />
                      <span className="text-sm text-white">{isEs ? '📹 Video Llamada' : '📹 Video Call'}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.needsSiteSurvey} 
                        onChange={(e) => updateField(null, 'needsSiteSurvey', e.target.checked)}
                        className="w-4 h-4 rounded border-2 border-white bg-transparent checked:bg-orange-500 checked:border-orange-500 cursor-pointer appearance-none relative
                          after:content-[''] after:absolute after:hidden checked:after:block after:left-[4px] after:top-[1px] after:w-[5px] after:h-[8px] after:border-white after:border-r-2 after:border-b-2 after:rotate-45"
                      />
                      <span className="text-sm text-white">{isEs ? '📍 Site Survey (Visita Técnica)' : '📍 Site Survey'}</span>
                    </label>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <Label className="text-xs text-gray-600">{isEs ? 'Archivos (Opcional)' : 'Files (Optional)'}</Label>
                  <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input type="file" id="file-upload" multiple accept="image/*,.pdf" onChange={handleFileChange} className="hidden" />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">{isEs ? 'Subir archivos' : 'Upload files'}</p>
                    </label>
                  </div>
                  {files.length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border-2 border-green-400 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-medium text-sm">
                          {isEs ? `${files.length} archivo(s) cargado(s) correctamente` : `${files.length} file(s) uploaded successfully`}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {files.map((file, i) => (
                          <div key={i} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm border border-green-200">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                                <FileText className="w-4 h-4 text-green-600" />
                              </div>
                              <span className="truncate font-medium text-gray-700">{file.name}</span>
                            </div>
                            <button type="button" onClick={() => removeFile(i)} className="text-red-500 hover:text-red-700 p-1"><X className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SMS Consent Checkbox */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.smsConsent}
                  onChange={(e) => setFormData(prev => ({ ...prev, smsConsent: e.target.checked }))}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  required
                />
                <span className="text-sm text-gray-600">
                  {isEs 
                    ? <>Acepto recibir notificaciones SMS de IQES sobre mi cotización y citas. Responda STOP para cancelar. Pueden aplicar tarifas de mensajes y datos. <a href="/privacy" className="text-blue-600 underline" target="_blank">Política de Privacidad</a> | <a href="/terms" className="text-blue-600 underline" target="_blank">Términos</a></>
                    : <>I agree to receive SMS notifications from IQES regarding my quote and appointments. Reply STOP to opt-out. Msg & data rates may apply. <a href="/privacy" className="text-blue-600 underline" target="_blank">Privacy Policy</a> | <a href="/terms" className="text-blue-600 underline" target="_blank">Terms</a></>
                  }
                </span>
              </label>
            </div>

            {/* Submit */}
            <div className="flex justify-center">
              <Button 
                type="submit" 
                disabled={loading || formData.selectedServices.length === 0 || !formData.smsConsent}
                className="w-full md:w-auto px-8 py-6 text-white text-lg rounded-full"
                style={{ backgroundColor: '#00A651' }}
              >
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{isEs ? 'Enviando...' : 'Sending...'}</> : <><Check className="w-5 h-5 mr-2" />{isEs ? 'Enviar Cotización' : 'Submit Quote'}</>}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
